/**
 * アプリのシェル。トークンゲート・同期のライフサイクル・ルーティングだけを持ち、
 * 画面の中身は src/pages/* に置く。
 *
 * 起動 →（トークンがあれば）GET /api/state → IndexedDB を置き換え → 以降は
 * IndexedDB を描画元にする。`visibilitychange` と手動リフレッシュで再取得（SPEC §2.1）。
 */
import { Suspense, lazy, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { BottomNav } from './components/BottomNav';
import { SnackbarProvider, useSnackbar } from './components/Snackbar';
import { TokenGate } from './components/TokenGate';
import { useShoppingCandidates } from './hooks/useCalc';
import { Home } from './pages/Home';
import { ApiError } from './lib/api';
import { wipeLocal } from './lib/db';
import { RouterProvider, useRouter } from './lib/router';
import { useSettings } from './lib/settings';
import { refreshFromServer, setUnauthorizedHandler } from './lib/sync';
import { clearToken, getToken } from './lib/token';

// 起動画面はホームなので、それ以外は必要になってから読む。
// recharts を含むダッシュボード・品目詳細を分離して初回表示を軽くする（SPEC §8）。
const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const ItemDetail = lazy(() => import('./pages/ItemDetail').then((m) => ({ default: m.ItemDetail })));
const ShoppingList = lazy(() => import('./pages/ShoppingList').then((m) => ({ default: m.ShoppingList })));
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));

type Gate = 'token' | 'app';

export default function App(): ReactNode {
  return (
    <SnackbarProvider>
      <RouterProvider>
        <Root />
      </RouterProvider>
    </SnackbarProvider>
  );
}

function Root(): ReactNode {
  const [gate, setGate] = useState<Gate>(() => (getToken() ? 'app' : 'token'));
  const [syncing, setSyncing] = useState(false);
  const snack = useSnackbar();

  const toTokenGate = useCallback(() => {
    clearToken();
    setGate('token');
  }, []);

  // リトライキューが 401 を受けたときもトークン入力へ戻す（SPEC §3）
  useEffect(() => {
    setUnauthorizedHandler(toTokenGate);
    return () => setUnauthorizedHandler(() => {});
  }, [toTokenGate]);

  const refresh = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!getToken()) return;
      setSyncing(true);
      try {
        await refreshFromServer();
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          toTokenGate();
        } else if (!opts.silent) {
          // オフラインでもキャッシュ済みデータで操作は続けられる
          snack({ message: e instanceof Error ? e.message : String(e), tone: 'error' });
        }
      } finally {
        setSyncing(false);
      }
    },
    [snack, toTokenGate],
  );

  // 起動時 / フォアグラウンド復帰時 / オンライン復帰時に再取得（SPEC §2.1）
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (gate !== 'app') return;

    void refreshRef.current({ silent: true });

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void refreshRef.current({ silent: true });
    };
    const onOnline = (): void => void refreshRef.current({ silent: true });

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [gate]);

  if (gate === 'token') {
    return (
      <TokenGate
        onAuthenticated={() => {
          // 別トークン＝別世帯のデータかもしれないので、キャッシュは捨ててから入る
          void wipeLocal().then(() => setGate('app'));
        }}
      />
    );
  }

  return <Shell syncing={syncing} onRefresh={() => void refresh()} />;
}

function Shell({ syncing, onRefresh }: { syncing: boolean; onRefresh: () => void }): ReactNode {
  const { route } = useRouter();
  const [settings] = useSettings();
  const candidates = useShoppingCandidates(settings.shoppingThresholdDays);
  const excluded = new Set(settings.excludedItemIds);
  const badge =
    candidates.filter((c) => !excluded.has(c.itemId)).length +
    settings.manualItemIds.filter((id) => !excluded.has(id) && !candidates.some((c) => c.itemId === id))
      .length;

  return (
    <>
      <Suspense fallback={<PageFallback />}>
        {route.name === 'dashboard' && <Dashboard />}
        {route.name === 'item' && <ItemDetail itemId={route.itemId} />}
        {route.name === 'shopping-list' && <ShoppingList />}
        {route.name === 'settings' && <Settings />}
        {route.name === 'home' && <Home syncing={syncing} onRefresh={onRefresh} />}
      </Suspense>
      <BottomNav shoppingCount={badge} />
    </>
  );
}

function PageFallback(): ReactNode {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <div className="size-6 animate-spin rounded-full border-2 border-ink-700 border-t-orange-500" />
    </div>
  );
}
